import { fetchRequest, fetchError, fetchSuccess, fetchRetry } from '../actions';
import retry from 'retry';
import environment from '../.config/environment';

import {
    AppError,
    FETCH_ERROR_NAME,
    AUTH_ERROR_TYPE,
    NOT_FOUND_ERROR_TYPE,
    SERVICE_ERROR_TYPE,
    INVALID_REQUEST_TYPE,
    NOT_AUTHORIZED_TYPE,
} from '../utils';

export const getErrorType = statusCode => {
    switch (statusCode) {
        case 400:
            return INVALID_REQUEST_TYPE;
        case 401:
            return NOT_AUTHORIZED_TYPE;
        case 403:
            return AUTH_ERROR_TYPE;
        case 404:
            return NOT_FOUND_ERROR_TYPE;
        case 409:
            return INVALID_REQUEST_TYPE;
        default:
            if (statusCode >= 500) return SERVICE_ERROR_TYPE;
            return undefined;
    }
};

const RETRYABLE_STATUS_CODES = [409, 429, 500];
const BASE_RETRY_OPTS = {
    retries: 2,
    factor: 2,
    randomize: true,
    minTimeout: 200,
    maxTimeout: 3 * 1000,
    retryableStatuses: [],
};

export const performFetch = async (dispatch, opts = {}) => {
    const { api, idToken, params = {}, retryOptions = {} } = opts;
    const mergedRetryOptions = {
        ...BASE_RETRY_OPTS,
        ...retryOptions,
    };
    const allRetryableStatuses = [
        ...RETRYABLE_STATUS_CODES,
        ...mergedRetryOptions.retryableStatuses,
    ];
    if (!idToken) {
        throw new AppError('"performFetch"" requires "idToken".', {
            type: AUTH_ERROR_TYPE,
        });
    }

    const { path = '', method = 'GET', headers = {}, body } = params;

    const {
        baseUrl,
        fetch: fetchFn = fetch,
        onSuccess = res => res.json && res.json(),
    } = api;

    dispatch(fetchRequest());

    const url = baseUrl + path;
    const preparedBody = body ? JSON.stringify(body) : undefined;
    const request = {
        method,
        retries: 5,
        retryDelay: 800,
        body: preparedBody,
        headers: {
            'Content-Type':
                method === 'POST'
                    ? 'application/x-www-form-urlencoded'
                    : 'application/json',
            Authorization: idToken,
            ...headers,
        },
    };

    const operation = retry.operation(mergedRetryOptions);

    operation.attempt(async retries => {
        try {
            const res = await fetchFn(url, request);

            //failure
            if (!res.ok) {
                // Retry
                if (
                    operation.retry(allRetryableStatuses.includes(res.status))
                ) {
                    dispatch(
                        fetchRetry({
                            retries,
                            type: getErrorType(res.status),
                            name: FETCH_ERROR_NAME,
                            status: res.status,
                        })
                    );
                    return;
                }

                throw new AppError(
                    `Request to url ${url} rejected with status: ${res.status}`,
                    {
                        type: getErrorType(res.status),
                        name: FETCH_ERROR_NAME,
                        status: res.status,
                    }
                );
            }

            // Success
            const data = await onSuccess(res);
            dispatch(fetchSuccess(data));
        } catch (err) {
            dispatch(fetchError(err));
        }
    });
};

/**
 * Fetch doesn't work with react-query natively since it requires the query
 * function to throw an error to trigger a retry. Simple wrapper around fetch
 * to throw error when status > 399 and throw the error the format expected in the
 * rest of the app
 * @param {string} path relative URL path, base url will prepended
 * @param {*} requestOpts opts to be passed to fetch
 * @param {*} globals optional argument, mostly used to prevent using window directly
 * @returns {Promise<any>}
 */
export const statelessPerformFetch = async (
    path,
    requestOpts,
    globals = window
) => {
    const { HOST_NAME: { CLASSROOM } = {} } = environment(globals);
    const url = CLASSROOM + path;
    const res = await globals.fetch(url, requestOpts);
    if (!res.ok) {
        const error = await res.json();
        throw new AppError(
            `Request to url ${url} rejected with status: ${res.status}`,
            {
                type: getErrorType(res.status),
                name: FETCH_ERROR_NAME,
                status: res.status,
                cause: error?.name,
            }
        );
    }
    if (res.status !== 204) {
        return await res.json();
    }
};
